import React, { useEffect, useState } from "react";
import { FileText, UserCheck, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, Loader, StatusBadge } from "@/components/dashboard/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSelect, SelectCheck, SelectionToolbar } from "@/components/admin/BulkBar";

export default function AdminRequests() {
  const { user, hasRole } = useAuth();
  const [requests, setRequests] = useState(null);
  const [missions, setMissions] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [staff, setStaff] = useState([]);
  const [arch, setArch] = useState(false);
  const sel = useSelect();

  const load = () => {
    api.get(`/requests?include_archived=${arch}`).then(({ data }) => setRequests((data || []).filter((r) => r.kind !== "project")));
    api.get("/missions").then(({ data }) => setMissions(data || []));
  };
  useEffect(() => { sel.clear(); load(); /* eslint-disable-next-line */ }, [arch]);

  const missionFor = (rid) => missions.find((m) => m.request_id === rid && m.status !== "Annulée");

  const openAssign = async (r) => {
    setAssignFor(r); setStaff([]);
    try { const { data } = await api.get(`/requests/${r.request_id}/available-staff`); setStaff(data || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const assign = async (staffId) => {
    try { await api.post(`/requests/${assignFor.request_id}/assign`, { staff_id: staffId }); toast.success("Mission attribuée"); setAssignFor(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const selfAssign = async (r) => {
    try { await api.post(`/requests/${r.request_id}/self-assign`); toast.success("Mission attribuée à vous-même"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (r, status) => {
    try { await api.put(`/requests/${r.request_id}`, { status }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const missionAct = async (m, status) => {
    try { await api.put(`/missions/${m.mission_id}`, { status }); toast.success("Mission " + status.toLowerCase()); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (requests === null) return <Loader />;
  const canSelf = hasRole("secretary") && user?.can_take_missions;

  return (
    <div>
      <PageHeader title="Demandes & dispatch" subtitle="Attribuez les missions horaires aux membres disponibles." />
      <SelectionToolbar items={requests} idKey="request_id" sel={sel} entity="requests" onDone={load} label="demande" archived={arch} onToggleArchived={() => setArch((a) => !a)} />
      {requests.length === 0 ? <EmptyState icon={FileText} title="Aucune demande horaire" /> : (
        <div className="space-y-4">
          {requests.map((r) => {
            const m = missionFor(r.request_id);
            const slot = r.dates?.[0];
            return (
              <div key={r.request_id} className={`relative bg-white rounded-xl border p-5 pl-11 ${sel.has(r.request_id) ? "border-primary ring-1 ring-primary/30" : "border-slate-200"}`} data-testid={`admin-request-${r.request_id}`}>
                <SelectCheck className="absolute left-3 top-6" checked={sel.has(r.request_id)} onChange={() => sel.toggle(r.request_id)} testId={`select-request-${r.request_id}`} />
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-900">{r.client_name || "Client"}</span>
                      <span className="text-sm text-slate-400">{r.client_practice}</span>
                      <StatusBadge status={r.status} />
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{(r.services || []).join(", ")}</p>
                    {slot && <p className="text-sm text-slate-500 mt-0.5">{new Date(slot.date).toLocaleDateString("fr-CH", { weekday: "long", day: "numeric", month: "long" })} · {slot.start}–{slot.end}</p>}
                    {r.description && <p className="text-sm text-slate-400 mt-1 italic">« {r.description} »</p>}
                    {m && <p className="text-sm mt-2 text-slate-700">Attribuée à <strong>{m.staff_name}</strong> — <StatusBadge status={m.status} /></p>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {!m && !["Annulée", "Refusée", "Terminée"].includes(r.status) && <>
                      <Button size="sm" className="bg-primary" onClick={() => openAssign(r)} data-testid={`assign-btn-${r.request_id}`}><UserCheck className="h-4 w-4 mr-1" />Assigner</Button>
                      {canSelf && <Button size="sm" variant="outline" onClick={() => selfAssign(r)} data-testid={`self-assign-${r.request_id}`}><Sparkles className="h-4 w-4 mr-1" />Me l'attribuer</Button>}
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setStatus(r, "Refusée")}>Refuser</Button>
                    </>}
                    {m && m.status === "Attribuée" && <>
                      <Button size="sm" className="bg-primary" onClick={() => missionAct(m, "Confirmée")} data-testid={`confirm-mission-${r.request_id}`}>Confirmer</Button>
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => missionAct(m, "Annulée")}>Annuler</Button>
                    </>}
                    {m && m.status === "Confirmée" && <Button size="sm" variant="outline" onClick={() => missionAct(m, "Annulée")}>Annuler la mission</Button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Membres disponibles</DialogTitle></DialogHeader>
          {staff.length === 0 ? <p className="text-sm text-slate-500 py-4">Aucun membre disponible sur ce créneau (Complet).</p> : (
            <div className="space-y-2">{staff.map((s) => (
              <div key={s.user_id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
                <span className="font-medium text-slate-800">{s.name}</span>
                <Button size="sm" className="bg-primary" onClick={() => assign(s.user_id)} data-testid={`assign-to-${s.user_id}`}>Attribuer</Button>
              </div>))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
